/**
 * EnableStateReporting — the "no metrics section" notice plus its one sanctioned fix.
 *
 * WHY (OP #1678): a gateway configured before the seeder emitted a `metrics` section (the office
 * gateway, 2026-10-08) reports state only at the TIP agent default — every 600 s — and every
 * Gateway preview warns "gateway will not report state: no metrics section". There was no way to
 * act on that warning. This is the action: intent `metrics.ensure` through the SAME generic
 * /gateway/plan + /gateway/apply write path every Gateway screen uses, so it inherits the plan
 * token, the invariants, the audit row (`device.gateway.metrics.ensure`) and the read-back.
 *
 * Contract mirrored from the server (services/gateway_config.transform_metrics_ensure):
 *   - adds Meridian's standard block (statistics 60 s [lldp, clients], health 120 s) only when
 *     the config has none; an existing block is never edited (apply refuses "nothing to change");
 *   - the diff is the `metrics` key and nothing else (no LLDP / interface / config-raw change);
 *   - preview first, nothing is sent until the operator pushes; standard ~4 min AP disruption.
 * Role: org_admin (GATEWAY_WRITE_ROLE on the server) — RoleGate disables, never hides.
 */
import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useApplyGatewayChange, usePlanGatewayChange } from "@/lib/api/hooks";
import type { GatewayApplyResult } from "@/types/domain";
import { Button } from "@/components/ui/Button";
import { ErrorBanner, EmptyState } from "@/components/ui/Page";
import { RoleGate } from "@/components/auth/RoleGate";
import { PlanWarnings, RawDiff } from "@/components/gateway/GatewayChrome";
import { PushDisruptionWarning } from "@/components/gateway/ReassertManaged";

/** Must match api/v2/device_gateway.GATEWAY_WRITE_ROLE. */
const WRITE_ROLE = "org_admin" as const;
const ROLE_REASON = "Changing a gateway configuration requires a customer admin.";
/** The intent and its (always empty) spec — the server refuses any spec for this intent. */
const INTENT = "metrics.ensure" as const;
const EMPTY_SPEC = {};

/**
 * The warning, where the gateway is shown, with the action beside it. Renders nothing unless
 * the server said `metrics_present === false` (undefined = unknown/older server: stay quiet).
 */
export function StateReportingNotice({
  serial,
  metricsPresent,
  name,
}: {
  serial: string;
  metricsPresent: boolean | undefined;
  /** Optional label when several gateways are listed together (site VLAN screen). */
  name?: string;
}) {
  const [open, setOpen] = useState(false);
  if (metricsPresent !== false) return null;
  return (
    <div role="status" className="md-card flex flex-wrap items-start gap-3 border-warn/40 bg-warn-soft p-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-ink">
          {name ? `${name}: ` : ""}no metrics section — state reports only every ~10 minutes
        </div>
        <div className="mt-1 text-ink-muted">
          Without periodic statistics/health this gateway falls back to the agent default, so its state, LLDP
          neighbours and config verification lag by up to 10 minutes.
        </div>
      </div>
      <RoleGate min={WRITE_ROLE} mode="disable" reason={ROLE_REASON}>
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Enable state reporting
        </Button>
      </RoleGate>
      {open && <EnableStateReportingDialog serial={serial} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** Preview (read-only plan) -> review -> push. One push, one audit row. */
function EnableStateReportingDialog({ serial, onClose }: { serial: string; onClose: () => void }) {
  const plan = usePlanGatewayChange(serial);
  const apply = useApplyGatewayChange(serial);
  const [done, setDone] = useState<GatewayApplyResult | null>(null);

  // Preview once on open. Read-only on the server: no push, no DB write.
  const { mutate: runPlan } = plan;
  useEffect(() => {
    runPlan({ intent: INTENT, spec: EMPTY_SPEC });
  }, [runPlan]);

  const planned = plan.data;
  // The block that would be added, straight from the server's candidate (not a client copy).
  const added = planned?.diff.find((d) => d.path === "metrics")?.after;

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Enable state reporting</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Adds Meridian's standard <span className="font-mono">metrics</span> section to {serial} so it reports
            state every 60 s and health every 120 s. Nothing else in the configuration changes. Nothing has been
            sent yet — this is the exact change.
          </Dialog.Description>

          {plan.isPending && <div className="mt-4 text-sm text-ink-muted">Working out what would change…</div>}
          {plan.isError && (
            <div className="mt-4">
              <ErrorBanner error={plan.error} />
            </div>
          )}

          {planned && !done && (
            <div className="mt-4 flex flex-col gap-3">
              {!planned.changed ? (
                <EmptyState
                  title="Already reporting"
                  hint="This gateway already has a metrics section. An existing one is never overwritten."
                />
              ) : (
                <>
                  {added !== undefined && (
                    <div className="md-card p-3 text-sm">
                      <div className="font-medium text-ink">
                        <span className="mr-1 font-bold text-healthy">+</span>metrics
                      </div>
                      <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[0.6875rem] text-ink-subtle">
                        {JSON.stringify(added, null, 1)}
                      </pre>
                    </div>
                  )}
                  <PlanWarnings warnings={planned.warnings} />
                  <RawDiff plan={planned} />
                  <PushDisruptionWarning />
                </>
              )}
              {apply.isError && <ErrorBanner error={apply.error} />}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={apply.isPending}>
                  Cancel
                </Button>
                <RoleGate min={WRITE_ROLE} mode="disable" reason={ROLE_REASON}>
                  <Button
                    variant="danger"
                    disabled={!planned.changed || apply.isPending}
                    onClick={() =>
                      apply.mutate(
                        { intent: INTENT, spec: EMPTY_SPEC, plan_token: planned.plan_token },
                        { onSuccess: (r) => setDone(r) },
                      )
                    }
                  >
                    {apply.isPending ? "Pushing…" : "Enable — push to gateway"}
                  </Button>
                </RoleGate>
              </div>
            </div>
          )}

          {done && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="md-card border-healthy/40 bg-healthy-soft p-4 text-sm">
                <div className="font-medium text-ink">Pushed to the gateway</div>
                <div className="mt-1 text-ink-muted">
                  owgw accepted the push. The metrics section itself is not echoed in device state, so the read-back
                  confirms the new configuration was picked up; fresh state should then arrive about every minute. APs
                  recover in about 4 minutes.
                </div>
              </div>
              <div className="flex justify-end">
                <Button variant="primary" onClick={onClose}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
